<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import SearchIcon from '@/assets/icons/shared/search.svg';
import PlusIcon from '@/assets/icons/shared/plus.svg';
import { uniqueId } from 'lodash-es';
import { INPUT_DEBOUNCE } from '@/modules/common/consts/ui.ts';
import type { QueryOptionsGetter, SelectEvent } from '@/modules/common/types/ui.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import UiDropdownList from '@/modules/ui/components/ui/UiDropdownList.vue';
import UiDropdown from '@/modules/ui/components/ui/UiDropdown.vue';
import UiInput from '@/modules/ui/components/ui/UiInput.vue';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { useI18n } from 'vue-i18n';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import { useDebounceFn } from '@vueuse/core';
import { notification } from '@/modules/common/composables/use-notification.ts';
import UiButtonIcon from '@/modules/ui/components/ui/UiButtonIcon.vue';
import UiSpinner from '@/modules/ui/components/ui/UiSpinner.vue';

const model = defineModel<Option | null>({ required: true, default: null });

const {
  id = uniqueId(IdPrefix.Control),
  label,
  getOptions,
  color = 'gray',
  size = 'm',
  placeholder = 'ui.search.selectValue',
  disabled,
  addOption,
  addOptionLabel,
} = defineProps<{
  id?: string;
  label?: string;
  error?: boolean;
  placeholder?: string;
  disabled?: boolean;
  getOptions: QueryOptionsGetter<string | number>;
  color?: 'gray' | 'white';
  size?: 'm' | 'l';
  noClear?: boolean;
  addOption?: (query: string) => void | Promise<void>;
  addOptionLabel?: string;
}>();

const emit = defineEmits<{
  focus: [];
  blur: [];
}>();

const { signal, abort } = useAbort();

const { maybeT } = useI18n();

const listRef = useTemplateRef('list');
const dropdownRef = useTemplateRef('dropdown');
const inputRef = useTemplateRef('uiInput');

const input = ref('');
const options = ref<Option[]>([]);
const isLoading = ref(false);
let optionsRequestId = 0;

const showAddOption = computed(
  () => !!addOption && !!addOptionLabel && !disabled && !isLoading.value && !options.value.length
);
const showEmptyState = computed(() => !isLoading.value && !options.value.length && !showAddOption.value);
const addOptionText = computed(() => addOptionLabel ?? '');

watch(model, (option) => (input.value = option?.label || ''));
watch(input, () => listRef.value?.setActive(null));

function open() {
  if (isLoading.value || input.value) {
    dropdownRef.value?.show();
  }
}

function close() {
  dropdownRef.value?.hide();
}

function onFocus() {
  open();
  emit('focus');
}

function onBlur() {
  if (model.value) {
    input.value = model.value.label;
  }
  emit('blur');
}

function onClear() {
  abort('Clear');
  optionsRequestId += 1;
  isLoading.value = false;
  options.value = [];
  model.value = null;
  close();
}

function onSelect({ option }: SelectEvent) {
  input.value = option.label;
  model.value = option;
  close();
}

async function onAddOption() {
  if (!addOption || disabled) {
    return;
  }

  const query = input.value;
  abort('Add option');
  optionsRequestId += 1;
  isLoading.value = false;
  input.value = '';
  options.value = [];
  model.value = null;
  close();
  await addOption(query);
}

const getOptionsDebounced = useDebounceFn(async function (query: string, requestId: number) {
  try {
    const nextOptions = await getOptions(query, signal.value);

    if (requestId !== optionsRequestId) {
      return;
    }

    options.value = nextOptions;
    isLoading.value = false;
  } catch (error) {
    if (requestId !== optionsRequestId) {
      return;
    }

    if (!ErrorUtil.isUnexpected(error)) {
      return;
    }

    isLoading.value = false;

    notification.error(
      { key: 'ui.notification.error.field.getOptions', params: { field: maybeT(label || placeholder) } },
      error
    );
  }
}, INPUT_DEBOUNCE);

function fetchOptions(query: string) {
  abort('A new value has been received');
  optionsRequestId += 1;

  if (!query) {
    isLoading.value = false;
    options.value = [];
    close();
    return;
  }

  const requestId = optionsRequestId;
  isLoading.value = true;
  open();
  void getOptionsDebounced(query, requestId);
}

function focus() {
  inputRef.value?.focus();
}

defineExpose({
  focus,
});
</script>

<template>
  <UiDropdown
    ref="dropdown"
    class="ui-search"
    :offset="4"
    shrink
    anchor-width
    full-width
    close-if-inner-click
    close-if-outside-click
  >
    <template #anchor>
      <UiInput
        :id
        ref="uiInput"
        v-model.trim="input"
        :placeholder="placeholder"
        :color="color"
        :size="size"
        :disabled="disabled"
        :error
        :no-clear="noClear"
        autocomplete="off"
        @update:model-value="fetchOptions"
        @focus="onFocus"
        @blur="onBlur"
        @clear="onClear"
        @keydown.tab="close"
      >
        <template #postfix>
          <UiButtonIcon
            class="search-action"
            color="gray"
            tabindex="-1"
            :error
            @mousedown.stop
            @click.stop="fetchOptions(input)"
          >
            <SearchIcon />
          </UiButtonIcon>
        </template>
      </UiInput>
    </template>

    <div class="body">
      <div
        v-if="isLoading"
        class="state"
      >
        <UiSpinner />
      </div>

      <div
        v-else-if="showEmptyState"
        class="state"
      >
        {{ $t('ui.search.nothingFound') }}
      </div>

      <template v-else-if="showAddOption">
        <button
          type="button"
          class="add-option"
          @mousedown.stop.prevent
          @click="onAddOption"
        >
          <PlusIcon class="add-option-icon" />
          <span>{{ $t(addOptionText) }}</span>
        </button>
      </template>

      <UiDropdownList
        v-else
        ref="list"
        :options="options"
        :checked-options="model"
        @select="onSelect"
      />
    </div>
  </UiDropdown>
</template>

<style scoped lang="scss">
.ui-search {
  :deep(.search-action) {
    flex-shrink: 0;
    cursor: pointer;
  }

  @media (hover: hover) {
    :deep(.ui-input:hover .search-action:not(:disabled)) {
      color: var(--color-gray-800);
    }

    :deep(.ui-input._error:hover .search-action:not(:disabled)) {
      color: var(--color-red-1000);
    }
  }
}

.body {
  display: flex;
  overflow: hidden;
  flex-direction: column;
  box-shadow: var(--shadow);
  border-radius: 8px;
}

.state {
  display: flex;
  min-height: 48px;
  align-items: center;
  justify-content: center;
}

.add-option {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 0;
  background: transparent;
  color: var(--color-gray-1000);
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
  text-align: left;
  cursor: pointer;

  @media (hover: hover) {
    &:hover {
      background-color: var(--color-gray-100);
    }
  }
}

.add-option-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  fill: var(--color-gray-1000);
}
</style>
